<template>
  <div class="scanner-wrapper">
    
    <!-- ================================================= -->
    <!-- الحالة 1: وضع الكاميرا (التقاط) -->
    <!-- ================================================= -->
    <div v-if="step === 'camera'" class="camera-mode-container">
      
      <div class="video-wrapper">
        <video ref="video" autoplay playsinline class="video-feed"></video>
        
        <!-- مربع التوجيه -->
        <div class="guide-box">
           <div class="corner top-left"></div>
           <div class="corner top-right"></div>
           <div class="corner bottom-left"></div>
           <div class="corner bottom-right"></div>
        </div>
        <div class="guide-text">ضع الورقة داخل الإطار</div>
      </div>

      <!-- أزرار الكاميرا -->
      <div class="camera-controls">
        <!-- إلغاء -->
        <button @click="stopCameraAndClose" class="btn btn-dark rounded-circle p-3 shadow-sm btn-icon">
          <i class="fas fa-times"></i>
        </button>

        <!-- التقاط (يذهب للقص) -->
        <button @click="captureForCrop" class="btn-capture">
          <div class="inner-circle"></div>
        </button>

        <!-- زر وهمي للتوازن -->
        <div style="width: 50px;"></div>
      </div>
    </div>

    <!-- ================================================= -->
    <!-- الحالة 2: وضع القص (Cropping) -->
    <!-- ================================================= -->
    <div v-else-if="step === 'crop'" class="camera-mode-container bg-dark">
      
      <div class="video-wrapper bg-black">
        <!-- مكون القص (Vue 2 Version) -->
        <cropper
          ref="cropper"
          class="cropper"
          :src="tempImage"
          :stencil-props="{
            aspectRatio: 0,
            movable: true,
            resizable: true
          }"
        />
      </div>

      <!-- أزرار التحكم في القص -->
      <div class="camera-controls justify-content-between px-4">
        <button @click="retake" class="btn btn-outline-light px-4 rounded-pill">
            <i class="fas fa-redo"></i> إعادة
        </button>
        
        <button @click="confirmCrop" class="btn btn-success px-4 rounded-pill fw-bold shadow">
            قص وحفظ <i class="fas fa-check"></i>
        </button>
      </div>
    </div>

    <!-- ================================================= -->
    <!-- الحالة 3: القائمة والمعاينة (List) -->
    <!-- ================================================= -->
    <div v-else class="preview-mode-container d-flex flex-column h-100">
      
      <!-- منطقة الصور (Scrollable) -->
      <div class="flex-grow-1 overflow-auto p-3" style="max-height: 60vh;">
        
        <!-- حالة فارغة -->
        <div v-if="capturedImages.length === 0" class="text-center py-5 border border-2 border-dashed rounded bg-light mt-4">
          <i class="fas fa-camera fa-3x text-muted mb-3 opacity-50"></i>
          <h5 class="fw-bold text-dark">جاهز للتصوير</h5>
          <p class="text-muted small">اضغط "إضافة صفحة" بالأسفل</p>
        </div>

        <!-- شبكة الصور -->
        <div v-else class="row g-2">
          <div v-for="(img, index) in capturedImages" :key="index" class="col-4 col-md-3">
            <div class="position-relative">
              <img :src="img.preview" class="img-fluid rounded border shadow-sm w-100" style="height: 100px; object-fit: cover;">
              <span class="badge bg-dark position-absolute top-0 start-0 m-1 rounded-circle" style="width: 20px; height: 20px; line-height: 14px; font-size: 10px;">{{ index + 1 }}</span>
              <button @click="removeImage(index)" class="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 m-1 rounded-circle shadow" style="width: 24px; height: 24px; line-height: 22px;">&times;</button>
            </div>
          </div>
        </div>

      </div>

      <!-- أزرار الإجراءات (Fixed Bottom) -->
      <div class="p-3 border-top bg-white mt-auto">
        <div class="row g-2">
           <div class="col-5">
              <button @click="startCamera" class="btn btn-outline-primary w-100 py-2">
                <i class="fas fa-plus"></i> صفحة
              </button>
           </div>
           <div class="col-7">
              <button @click="uploadAll" class="btn btn-success w-100 py-2 fw-bold" :disabled="isUploading || capturedImages.length === 0">
                <span v-if="isUploading" class="spinner-border spinner-border-sm"></span>
                <span v-else>حفظ ورفع ({{ capturedImages.length }}) <i class="fas fa-cloud-upload-alt"></i></span>
              </button>
           </div>
        </div>
      </div>

    </div>

    <!-- كانفاس مخفي -->
    <canvas ref="canvas" style="display: none;"></canvas>

  </div>
</template>

<script>
// استيراد مكتبة القص (إصدار Vue 2)
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import api from '@/api/axios';

export default {
  name: 'MultiPageScanner',
  components: { Cropper },
  props: {
    subjectId: { type: [Number, String], required: true }
  },
  data() {
    return {
      step: 'list', // list, camera, crop
      isCameraOpen: false,
      isUploading: false,
      capturedImages: [],
      stream: null,
      tempImage: null // لتخزين الصورة قبل القص
    };
  },
  methods: {
    // 1. بدء الكاميرا
    async startCamera() {
      this.step = 'camera';
      this.isCameraOpen = true;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } 
        });
        this.$nextTick(() => { if(this.$refs.video) this.$refs.video.srcObject = this.stream; });
      } catch (error) {
        alert("تعذر الوصول للكاميرا");
        this.step = 'list';
      }
    },

    // 2. التقاط الصورة (والذهاب للقص)
    captureForCrop() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!video) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // تحويل لـ Base64 وعرضه في الكروبر
      this.tempImage = canvas.toDataURL('image/jpeg');
      
      this.stopCamera(); // نوقف الكاميرا لتوفير البطارية
      this.step = 'crop'; // ننتقل لشاشة القص
    },

    // 3. تأكيد القص (الحفظ في القائمة)
    confirmCrop() {
      const { canvas } = this.$refs.cropper.getResult();
      if (canvas) {
        canvas.toBlob((blob) => {
            this.capturedImages.push({
                blob: blob,
                preview: URL.createObjectURL(blob)
            });
            this.step = 'list'; // العودة للقائمة
        }, 'image/jpeg', 0.9);
      }
    },

    // 4. إعادة التصوير (من شاشة القص)
    retake() {
        this.step = 'camera';
        this.startCamera();
    },

    // 5. إغلاق كامل
    stopCameraAndClose() {
        this.stopCamera();
        this.step = 'list';
    },

    stopCamera() {
      if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
          this.stream = null;
      }
      this.isCameraOpen = false;
    },

    removeImage(index) {
        this.capturedImages.splice(index, 1);
    },

    async uploadAll() {
      if (this.capturedImages.length === 0) return;
      this.isUploading = true;
      const formData = new FormData();
      formData.append('appeal_subject_id', this.subjectId);
      this.capturedImages.forEach((imgObj, index) => {
        formData.append('images[]', imgObj.blob, `page_${index}.jpg`);
      });

      try {
        await api.post('/appeals/upload-paper', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.$emit('upload-success');
        this.capturedImages = [];
      } catch (error) {
        alert("حدث خطأ أثناء الرفع");
      } finally {
        this.isUploading = false;
      }
    }
  },
  beforeDestroy() {
    this.stopCamera();
  }
};
</script>

<style scoped>
.scanner-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 1. الحاوية المشتركة للكاميرا والقص (بالمقاس المتجاوب) */
.camera-mode-container {
  display: flex;
  flex-direction: column;
  height: 85vh; /* ارتفاع مناسب للموبايل */
  background-color: #000;
  position: relative;
}

/* منطقة العرض (الفيديو أو الكروبر) */
.video-wrapper {
  flex-grow: 1;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 20px 20px;
}

.video-feed { width: 100%; height: 100%; object-fit: cover; }
.cropper { height: 100%; background: #000; }

/* 2. شريط التحكم السفلي */
.camera-controls {
  height: 100px;
  background-color: #000;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 10px;
  z-index: 100;
}

/* 3. مربع التوجيه */
.guide-box {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 80%; height: 70%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 10;
}
.guide-text {
  position: absolute; bottom: 20%; width: 100%; text-align: center;
  color: white; z-index: 11; font-weight: bold; text-shadow: 1px 1px 3px black;
}
.corner { position: absolute; width: 20px; height: 20px; border-color: #0d6efd; border-style: solid; }
.top-left { top: -2px; left: -2px; border-width: 4px 0 0 4px; }
.top-right { top: -2px; right: -2px; border-width: 4px 4px 0 0; }
.bottom-left { bottom: -2px; left: -2px; border-width: 0 0 4px 4px; }
.bottom-right { bottom: -2px; right: -2px; border-width: 0 4px 4px 0; }

/* زر الالتقاط */
.btn-capture {
  width: 70px; height: 70px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.8);
  background: transparent;
  padding: 4px;
  display: flex; justify-content: center; align-items: center;
}
.btn-capture .inner-circle {
  width: 100%; height: 100%;
  background-color: white;
  border-radius: 50%;
  transition: 0.1s;
}
.btn-capture:active .inner-circle { transform: scale(0.9); background-color: #dc3545; }

.btn-icon { width: 45px; height: 45px; display: flex; justify-content: center; align-items: center; }
</style>